import React, { useState, useEffect } from "react";
import { Clock, TrendingUp, Users, AlertCircle, Info } from "lucide-react";
import {
  ParentChildStats,
  getParentChildrenTiming,
  ChildTimingStats,
  ParentTimingResponse,
} from "../../../server/parent-dashboard/requests";

// Interface locale pour les données de timing
interface TimeData {
  label: string;
  value: number; // en heures
  hours: number;
  minutes: number;
  seconds: number;
}

// Fonction pour convertir le temps en heures
const convertTimeToHours = (time: {
  hours: number;
  minutes: number;
  seconds: number;
}) => {
  return time.hours + time.minutes / 60 + time.seconds / 3600;
};

// Fonction pour normaliser les données de temps
const normalizeTimeData = (
  timeData: any,
): { hours: number; minutes: number; seconds: number } => {
  if (!timeData || typeof timeData !== "object") {
    return { hours: 0, minutes: 0, seconds: 0 };
  }

  let hours = Number.isFinite(timeData.hours) ? Math.max(0, timeData.hours) : 0;
  let minutes = Number.isFinite(timeData.minutes)
    ? Math.max(0, timeData.minutes)
    : 0;
  let seconds = Number.isFinite(timeData.seconds)
    ? Math.max(0, timeData.seconds)
    : 0;

  // Normaliser les secondes qui dépassent 60
  if (seconds >= 60) {
    const additionalMinutes = Math.floor(seconds / 60);
    minutes += additionalMinutes;
    seconds = seconds % 60;
  }

  // Normaliser les minutes qui dépassent 60
  if (minutes >= 60) {
    const additionalHours = Math.floor(minutes / 60);
    hours += additionalHours;
    minutes = minutes % 60;
  }

  return { hours, minutes, seconds };
};

// Mapping des jours anglais vers français
const dayMapping: { [key: string]: string } = {
  Monday: "Lun",
  Tuesday: "Mar",
  Wednesday: "Mer",
  Thursday: "Jeu",
  Friday: "Ven",
  Saturday: "Sam",
  Sunday: "Dim",
};

// Mapping des mois anglais vers français
const monthMapping: { [key: string]: string } = {
  January: "Jan",
  February: "Fév",
  March: "Mar",
  April: "Avr",
  May: "Mai",
  June: "Juin",
  July: "Juil",
  August: "Août",
  September: "Sep",
  October: "Oct",
  November: "Nov",
  December: "Déc",
};

interface ChildrenTimeBarProps {
  selectedChildIndex: number;
  childrenStats: ParentChildStats[];
}

const ChildrenTimeBar: React.FC<ChildrenTimeBarProps> = ({
  selectedChildIndex,
  childrenStats,
}) => {
  const [selectedPeriod, setSelectedPeriod] = useState<
    "daily" | "weekly" | "monthly"
  >("daily");
  const [animateBars, setAnimateBars] = useState(false);
  const [timingData, setTimingData] = useState<ChildTimingStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  console.log("ChildrenTimeBar - État actuel:", {
    selectedPeriod,
    childrenStats,
    selectedChildIndex,
  });

  // Récupérer les données de timing depuis la route /statistics/timing
  useEffect(() => {
    const fetchTimingData = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await getParentChildrenTiming();

        if (
          response.status === 200 &&
          response.body &&
          "childrenStats" in response.body
        ) {
          setTimingData((response.body as ParentTimingResponse).childrenStats);
          console.log(
            "✅ Données de timing récupérées:",
            (response.body as ParentTimingResponse).childrenStats,
          );
        } else {
          console.warn("⚠️ Réponse invalide de la route timing:", response);
          setError("Impossible de récupérer les données de timing");
        }
      } catch (err) {
        console.error(
          "❌ Erreur lors de la récupération des données de timing:",
          err,
        );
        setError("Erreur lors de la récupération des données de timing");
      } finally {
        setLoading(false);
      }
    };

    fetchTimingData();
  }, []);

  // Fonction pour formater le temps de manière standardisée
  const formatTime = (hours: number, minutes: number, seconds: number) => {
    if (hours > 0) {
      if (minutes > 0) {
        return `${hours}h ${minutes}m`;
      } else {
        return `${hours}h`;
      }
    } else if (minutes > 0) {
      if (seconds > 0) {
        return `${minutes}m ${seconds}s`;
      } else {
        return `${minutes}m`;
      }
    } else if (seconds > 0) {
      return `${seconds}s`;
    } else {
      return "0s";
    }
  };

  const handlePeriodChange = (period: "daily" | "weekly" | "monthly") => {
    console.log("Changement de période vers:", period);
    setSelectedPeriod(period);
    setAnimateBars(false);
    setTimeout(() => setAnimateBars(true), 100);
  };

  // Démarrer l'animation quand les données changent
  useEffect(() => {
    if (childrenStats.length > 0) {
      setAnimateBars(true);
    }
  }, [childrenStats]);

  // Afficher le loading
  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6 h-96 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#131CC9] mx-auto mb-4"></div>
          <div className="text-gray-600 font-medium mb-2">
            Chargement des données de timing...
          </div>
          <div className="text-gray-500 text-sm">
            Récupération des statistiques de temps de vos enfants.
          </div>
        </div>
      </div>
    );
  }

  // Afficher l'erreur
  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6 h-96 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-400 mb-4">
            <AlertCircle className="w-16 h-16 mx-auto" />
          </div>
          <div className="text-gray-600 font-medium mb-2">
            Erreur de chargement
          </div>
          <div className="text-gray-500 text-sm mb-4">{error}</div>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-[#131CC9] text-white rounded-lg hover:bg-[#0F1A9E] transition-colors"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  // Afficher si aucun enfant n'est trouvé
  if (childrenStats.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6 h-96 flex items-center justify-center">
        <div className="text-center">
          <div className="text-gray-400 mb-4">
            <Users className="w-16 h-16 mx-auto" />
          </div>
          <div className="text-gray-600 font-medium mb-2">
            Aucun enfant trouvé
          </div>
          <div className="text-gray-500 text-sm">
            Aucun enfant n'est associé à votre compte pour le moment.
          </div>
        </div>
      </div>
    );
  }

  // Vérifier que l'index sélectionné est valide
  if (selectedChildIndex < 0 || selectedChildIndex >= childrenStats.length) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6 h-96 flex items-center justify-center">
        <div className="text-center">
          <div className="text-gray-400 mb-4">
            <Users className="w-16 h-16 mx-auto" />
          </div>
          <div className="text-gray-600 font-medium mb-2">
            Aucun enfant sélectionné
          </div>
          <div className="text-gray-500 text-sm">
            Veuillez sélectionner un enfant dans le dropdown pour voir ses
            statistiques de timing.
          </div>
        </div>
      </div>
    );
  }

  const selectedChild = childrenStats[selectedChildIndex];

  // Trouver les données de timing correspondantes à l'enfant sélectionné
  const selectedChildTiming = timingData.find(
    (child) => child.childName === selectedChild.childName,
  );

  // Si pas de données de timing pour cet enfant, utiliser les données de test
  const timingStats = selectedChildTiming?.stats || selectedChild.stats;

  // Fonction pour traiter les données journalières
  const processDailyData = (dailyData: any): TimeData[] => {
    if (!dailyData || typeof dailyData !== "object") {
      return [];
    }

    const result: TimeData[] = [];
    const days = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

    // Prioriser les données actuelles sur les données passées
    const currentWeek = dailyData.currentWeek || {};
    const lastWeek = dailyData.lastWeek || {};

    console.log("📅 Traitement journalier - Semaine actuelle:", currentWeek);
    console.log("📅 Traitement journalier - Semaine passée:", lastWeek);

    days.forEach((day) => {
      // Chercher la clé correspondante dans le mapping
      const englishDay = Object.keys(dayMapping).find(
        (key) => dayMapping[key] === day,
      );

      if (englishDay) {
        // Chercher dans la semaine actuelle d'abord, puis dans la semaine passée
        const timeData = currentWeek[englishDay] || lastWeek[englishDay];

        if (
          timeData &&
          typeof timeData === "object" &&
          (timeData.hours > 0 || timeData.minutes > 0 || timeData.seconds > 0)
        ) {
          const normalizedTime = normalizeTimeData(timeData);
          const hours = convertTimeToHours(normalizedTime);

          result.push({
            label: day,
            value: hours,
            ...normalizedTime,
          });
          console.log(
            `✅ ${day} (${englishDay}): ${normalizedTime.hours}h ${
              normalizedTime.minutes
            }m ${normalizedTime.seconds}s (${hours.toFixed(2)}h total)`,
          );
        } else {
          result.push({
            label: day,
            value: 0,
            hours: 0,
            minutes: 0,
            seconds: 0,
          });
          console.log(
            `⚠️ ${day} (${englishDay}): 0h (pas de données ou données vides)`,
          );
        }
      } else {
        result.push({
          label: day,
          value: 0,
          hours: 0,
          minutes: 0,
          seconds: 0,
        });
        console.log(`❌ ${day}: 0h (mapping non trouvé)`);
      }
    });

    console.log("📊 Résultat final journalier:", result);
    return result;
  };

  // Fonction pour traiter les données hebdomadaires
  const processWeeklyData = (weeklyData: any): TimeData[] => {
    if (!weeklyData || typeof weeklyData !== "object") {
      return [];
    }

    const result: TimeData[] = [];

    // Prioriser les données actuelles
    const currentMonth = weeklyData.currentMonth || {};
    const lastMonth = weeklyData.lastMonth || {};

    const allWeeks = { ...currentMonth, ...lastMonth };
    console.log("📅 Traitement hebdomadaire - Toutes les semaines:", allWeeks);

    // Trier les semaines par ordre logique
    const weekEntries = Object.entries(allWeeks)
      .filter(([_, timeData]) => {
        if (!timeData || typeof timeData !== "object") return false;
        const timeObj = timeData as {
          hours?: number;
          minutes?: number;
          seconds?: number;
        };
        return (
          (timeObj.hours && timeObj.hours > 0) ||
          (timeObj.minutes && timeObj.minutes > 0) ||
          (timeObj.seconds && timeObj.seconds > 0)
        );
      })
      .sort(([a], [b]) => {
        // Extraire le numéro de semaine
        const weekNumA = parseInt(a.replace(/\D/g, ""));
        const weekNumB = parseInt(b.replace(/\D/g, ""));
        return weekNumA - weekNumB;
      });

    console.log("📅 Semaines filtrées avec données:", weekEntries);

    weekEntries.forEach(([weekKey, timeData]: [string, any]) => {
      const normalizedTime = normalizeTimeData(
        timeData as { hours: number; minutes: number; seconds: number },
      );
      const hours = convertTimeToHours(normalizedTime);

      // Normaliser le label de la semaine
      let label: string;
      if (weekKey.includes(" ")) {
        const parts = weekKey.split(" ");
        label = `Sem ${parts[parts.length - 1]}`;
      } else if (weekKey.startsWith("week")) {
        label = weekKey.replace("week", "Sem ");
      } else {
        label = `Sem ${weekKey}`;
      }

      result.push({
        label,
        value: hours,
        ...normalizedTime,
      });
      console.log(
        `✅ ${weekKey} -> ${label}: ${normalizedTime.hours}h ${
          normalizedTime.minutes
        }m ${normalizedTime.seconds}s (${hours.toFixed(2)}h total)`,
      );
    });

    const finalResult = result.slice(0, 4); // Limiter à 4 semaines
    console.log("📊 Résultat final hebdomadaire:", finalResult);
    return finalResult;
  };

  // Fonction pour traiter les données mensuelles
  const processMonthlyData = (monthlyData: any): TimeData[] => {
    if (!monthlyData || typeof monthlyData !== "object") {
      return [];
    }

    const result: TimeData[] = [];

    // Prioriser les données actuelles
    const currentYear = monthlyData.currentYear || {};
    const lastYear = monthlyData.lastYear || {};

    const allMonths = { ...currentYear, ...lastYear };
    console.log("📅 Traitement mensuel - Tous les mois:", allMonths);

    // Ordre des mois
    const monthOrder = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];

    // Trier les mois par ordre chronologique
    const monthEntries = Object.entries(allMonths)
      .filter(([_, timeData]) => {
        if (!timeData || typeof timeData !== "object") return false;
        const timeObj = timeData as {
          hours?: number;
          minutes?: number;
          seconds?: number;
        };
        return (
          (timeObj.hours && timeObj.hours > 0) ||
          (timeObj.minutes && timeObj.minutes > 0) ||
          (timeObj.seconds && timeObj.seconds > 0)
        );
      })
      .sort(([a], [b]) => {
        const indexA = monthOrder.indexOf(a);
        const indexB = monthOrder.indexOf(b);
        return indexA - indexB;
      });

    console.log("📅 Mois filtrés avec données:", monthEntries);

    monthEntries.forEach(([monthKey, timeData]: [string, any]) => {
      const normalizedTime = normalizeTimeData(
        timeData as { hours: number; minutes: number; seconds: number },
      );
      const hours = convertTimeToHours(normalizedTime);

      // Utiliser le mapping français ou prendre les 3 premières lettres
      const label = monthMapping[monthKey] || monthKey.substring(0, 3);

      result.push({
        label,
        value: hours,
        ...normalizedTime,
      });
      console.log(
        `✅ ${monthKey} -> ${label}: ${normalizedTime.hours}h ${
          normalizedTime.minutes
        }m ${normalizedTime.seconds}s (${hours.toFixed(2)}h total)`,
      );
    });

    const finalResult = result.slice(0, 6); // Limiter à 6 mois
    console.log("📊 Résultat final mensuel:", finalResult);
    return finalResult;
  };

  // Traiter les données selon la période sélectionnée
  let currentData: TimeData[] = [];

  console.log("🔍 Traitement des données pour la période:", selectedPeriod);
  console.log("🔍 Données de timing disponibles:", timingStats);

  switch (selectedPeriod) {
    case "daily":
      currentData = processDailyData(timingStats.daylyTimeSpent);
      break;
    case "weekly":
      currentData = processWeeklyData(timingStats.weeklyTimeSpent);
      break;
    case "monthly":
      currentData = processMonthlyData(timingStats.monthlyTimeSpent);
      break;
    default:
      currentData = [];
  }

  console.log("📊 Données finales pour l'affichage:", currentData);

  // Calculer les statistiques de l'enfant sélectionné
  const calculateChildStats = () => {
    if (currentData.length === 0) return { meanHours: 0, meanMinutes: 0 };

    const totalValue = currentData.reduce(
      (sum: number, item: TimeData) => sum + item.value,
      0,
    );
    const meanValue = totalValue / currentData.length;
    const meanHours = Math.floor(meanValue);
    const meanMinutes = Math.floor((meanValue - meanHours) * 60);

    return { meanHours, meanMinutes };
  };

  const childStats = calculateChildStats();

  // Fonction pour obtenir le message approprié selon la période
  const getNoDataMessage = () => {
    const childName = selectedChild.childName;

    return {
      title: "Aucune donnée de temps",
      message: `${childName} n'a pas encore enregistré de données de temps sur la plateforme.`,
      suggestion:
        "Les statistiques apparaîtront dès que l'enfant commencera à utiliser la plateforme.",
    };
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center mb-8 space-y-4 lg:space-y-0">
        {/* Section temps moyen de l'enfant */}
        <div className="flex-1">
          <h3 className="text-[#131CC9] text-lg font-bold mb-3 flex items-center">
            <Users className="mr-2" size={20} />
            Temps moyen pour {selectedChild.childName}
          </h3>
          <div className="flex items-center space-x-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-800">
                {childStats.meanHours}
              </div>
              <div className="text-sm text-gray-500 font-medium">heures</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-800">
                {childStats.meanMinutes}
              </div>
              <div className="text-sm text-gray-500 font-medium">minutes</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-800">
                {currentData.length}
              </div>
              <div className="text-sm text-gray-500 font-medium">
                période(s)
              </div>
            </div>
          </div>
        </div>

        {/* Sélecteur de période */}
        <div className="lg:w-48">
          <select
            value={selectedPeriod}
            onChange={(e) =>
              handlePeriodChange(
                e.target.value as "daily" | "weekly" | "monthly",
              )
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#131CC9] focus:border-transparent"
          >
            <option value="daily">Journalière</option>
            <option value="weekly">Hebdomadaire</option>
            <option value="monthly">Mensuel</option>
          </select>
        </div>
      </div>

      {/* Graphique pour l'enfant sélectionné */}
      {currentData.length === 0 ? (
        <div className="border border-gray-200 rounded-lg p-8">
          <div className="text-center">
            <div className="text-gray-400 mb-4">
              <Info className="w-16 h-16 mx-auto" />
            </div>
            <div className="text-gray-600 font-medium mb-2">
              {getNoDataMessage().title}
            </div>
            <div className="text-gray-500 text-sm mb-4">
              {getNoDataMessage().message}
            </div>
            <div className="text-blue-600 text-xs bg-blue-50 px-4 py-2 rounded-lg inline-block">
              💡 {getNoDataMessage().suggestion}
            </div>
          </div>
        </div>
      ) : (
        <div className="border border-gray-200 rounded-lg p-6">
          {/* En-tête de l'enfant */}
          <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center mb-6 space-y-2 lg:space-y-0">
            <h4 className="text-lg font-semibold text-gray-800">
              {selectedChild.childName}
            </h4>
            <div className="flex items-center space-x-4 text-sm">
              <div className="text-center">
                <div className="font-bold text-gray-800">
                  {childStats.meanHours}h {childStats.meanMinutes}m
                </div>
                <div className="text-gray-500">moyenne</div>
              </div>
            </div>
          </div>

          {/* Graphique en barres horizontales */}
          <div className="space-y-4">
            {currentData.map((item, index) => {
              const maxValue = Math.max(
                ...currentData.map((item) => item.value),
              );
              const percentage =
                maxValue > 0 ? (item.value / maxValue) * 100 : 0;
              const displayValue = formatTime(
                item.hours,
                item.minutes,
                item.seconds,
              );

              return (
                <div key={index} className="space-y-2 group">
                  <div className="flex justify-between text-sm">
                    <span className="font-medium text-gray-700">
                      {item.label}
                    </span>
                    <span className="text-gray-500">
                      {Number.isFinite(item.value) ? displayValue : "Néant"}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3 relative overflow-hidden">
                    <div
                      className="bg-gradient-to-r from-[#131CC9] to-[#4F46E5] h-3 rounded-full transition-all duration-1000 ease-out relative"
                      style={{
                        width: animateBars
                          ? `${Number.isFinite(percentage) ? percentage : 0}%`
                          : "0%",
                        transitionDelay: `${index * 50}ms`,
                      }}
                    >
                      {/* Effet de brillance */}
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent opacity-50"></div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Informations supplémentaires pour l'enfant */}
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="text-sm text-gray-500 text-center">
              {currentData.filter((item: TimeData) => item.value > 0).length}{" "}
              période(s) avec activité
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChildrenTimeBar;
